import { Button, Form, Spinner } from 'react-bootstrap';
import { CustomerFunderCsv, VCOFunder, VCOProject } from './CustomProjectModels';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import { AddFilled } from '@carbon/icons-react';
import ApiHelper from '../../../Shared/ApiHelper';
import DatePicker from 'react-datepicker';
import DeleteButton from '../../../Shared/components/DeleteButton';
import SaveButton from '../../../Shared/components/SaveButton';
import Select from 'react-select';
import { SelectOption } from '../../../Shared/SharedModels';
import { UserProfileContext } from '../../../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

export interface FunderEditFormProps {
    vcoProject: VCOProject;
    batchKey: number;
}

function getFunderOptionLabel(csv: CustomerFunderCsv): string {
    if (!csv.funderID) return 'New Funder';

    return `Funder ID: ${csv.funderID} - ${csv.endDate ? 'Inactive' : 'Active'}`;
}

const FunderEditForm: FunctionComponent<FunderEditFormProps> = (props: FunderEditFormProps) => {
    const { vcoProject, batchKey } = props;

    const userProfile = useContext(UserProfileContext);

    const defaultFunderCsv = useMemo<CustomerFunderCsv>(
        () => ({
            batchKey: 0,
            sourceSystemShortName: '',
            projectID: '',
        }),
        []
    );

    const [selectedFunder, setSelectedFunder] = useState<CustomerFunderCsv | undefined>();
    const [funderOptions, setFunderOptions] = useState<SelectOption<CustomerFunderCsv>[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [canSave, setCanSave] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const api = useMemo(() => new ApiHelper(), []);

    const { instance } = useMsal();

    useEffect(() => {
        setCanSave(!!selectedFunder?.projectID && !!selectedFunder?.startDate);
    }, [selectedFunder?.projectID, selectedFunder?.startDate]);

    useEffect(() => {
        if (isLoading && vcoProject.projectKey && batchKey) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                process.env.REACT_APP_NET_API_URL + `/CustomProject/projects/${vcoProject.projectKey}/funder`,
                'GET'
            )
                .then(async (result: Response) => {
                    const funders: VCOFunder[] = await result.json();
                    const customFunders: CustomerFunderCsv[] = funders.map<CustomerFunderCsv>((vco) => {
                        const csv: CustomerFunderCsv = {
                            batchKey: batchKey,
                            funderID: vco.funderID,
                            sourceSystemShortName:
                                userProfile.sourceSystems.find((s) => s.sourceSystemKey === vcoProject.sourceSystemKey)
                                    ?.sourceSystemShortName ?? '',
                            projectID: vco.projectID ?? '',
                            funder: vco.fundingSourceKey,
                            otherFunder: vco.otherFunder,
                            grantID: vco.grantID,
                            startDate: vco.startDate,
                            endDate: vco.endDate,
                            dateCreated: vco.dateCreated,
                            dateUpdated: vco.dateUpdated,
                            userID: vco.userID,
                            dateDeleted: vco.dateDeleted,
                        };

                        return csv;
                    });

                    if (customFunders.length) {
                        setFunderOptions(
                            customFunders
                                .map((csv) => {
                                    const option: SelectOption<CustomerFunderCsv> = {
                                        label: getFunderOptionLabel(csv),
                                        value: csv,
                                    };

                                    return option;
                                })
                                .sort((a, b) => {
                                    if (a.value.endDate && b.value.endDate) {
                                        return 0;
                                    } else if (a.value.endDate) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                })
                        );

                        setSelectedFunder(customFunders[0]);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        api,
        batchKey,
        instance,
        isLoading,
        userProfile.sourceSystems,
        vcoProject.projectKey,
        vcoProject.sourceSystemKey,
    ]);

    return (
        <div className="project-card mb-5">
            <div className="project-card-header">
                <h3>Funder CSV Data</h3>

                {!!batchKey && !isLoading && (
                    <div className="flex-row">
                        {selectedFunder?.funderID && (
                            <DeleteButton
                                isLoading={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const dateDeleted = new Date().toISOString();

                                    const payload: CustomerFunderCsv = {
                                        ...selectedFunder,
                                        dateDeleted: dateDeleted,
                                        dateUpdated: dateDeleted,
                                        startDate: selectedFunder.startDate?.split('T')[0],
                                        endDate: selectedFunder.endDate?.split('T')[0],
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        process.env.REACT_APP_NET_API_URL + `/CustomProject/funder`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Funder Data Deleted Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}

                        <Button
                            className="form-action-button mr-2 ml-2"
                            variant="outline-primary"
                            disabled={isSaving}
                            onClick={() => {
                                const currentDate = new Date().toISOString().split('T')[0];
                                const newFunder: CustomerFunderCsv = {
                                    batchKey: batchKey,
                                    sourceSystemShortName:
                                        userProfile.sourceSystems.find(
                                            (s) => s.sourceSystemKey === vcoProject.sourceSystemKey
                                        )?.sourceSystemShortName ?? '',
                                    projectID: vcoProject.projectId ?? '',
                                    dateCreated: currentDate,
                                    dateUpdated: currentDate,
                                    userID: vcoProject.userID,
                                };

                                setSelectedFunder(newFunder);
                            }}
                        >
                            <AddFilled size={24} className="button-icon-padding" /> Add Funder
                        </Button>

                        {selectedFunder && (
                            <SaveButton
                                isSaving={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const payload: CustomerFunderCsv = {
                                        ...selectedFunder,
                                        startDate: selectedFunder.startDate?.split('T')[0],
                                        endDate: selectedFunder.endDate?.split('T')[0],
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        process.env.REACT_APP_NET_API_URL + `/CustomProject/funder`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Funder Data Saved Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            {isLoading ? (
                <Spinner
                    className="centered-spinner"
                    variant="primary"
                    size="sm"
                    animation="border"
                    data-testid="funderEditFormSpinner"
                />
            ) : (
                <div className="project-card-body">
                    {funderOptions.length || selectedFunder ? (
                        <>
                            <Form.Group className="wide-card-form-group">
                                <Form.Label className={isSaving ? 'disabled-label' : ''}>Funder ID</Form.Label>
                                <Select
                                    isClearable
                                    isLoading={isLoading}
                                    isDisabled={isSaving}
                                    className="filterSelect"
                                    placeholder="Select Funder..."
                                    value={
                                        selectedFunder
                                            ? {
                                                  label: getFunderOptionLabel(selectedFunder),
                                                  value: selectedFunder,
                                              }
                                            : undefined
                                    }
                                    options={funderOptions}
                                    onChange={(selectedOption) => {
                                        setSelectedFunder(selectedOption?.value);
                                    }}
                                />
                            </Form.Group>

                            <Form className="card-form">
                                {selectedFunder && (
                                    <>
                                        <Form.Group className="card-form-group">
                                            <Form.Label>Funder Source</Form.Label>
                                            <Form.Control
                                                data-testid="funderInput"
                                                placeholder="Enter Funder"
                                                value={selectedFunder.funder ?? ''}
                                                onChange={(event) => {
                                                    const value = parseInt(event.target.value);
                                                    const isNumber = !isNaN(value);
                                                    if (isNumber) {
                                                        setSelectedFunder((prev) => {
                                                            return {
                                                                ...defaultFunderCsv,
                                                                ...prev,
                                                                funder: value,
                                                            };
                                                        });
                                                    } else if (event.target.value === '') {
                                                        setSelectedFunder((prev) => {
                                                            return {
                                                                ...defaultFunderCsv,
                                                                ...prev,
                                                                funder: undefined,
                                                            };
                                                        });
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>Other Funder</Form.Label>
                                            <Form.Control
                                                data-testid="otherFunderInput"
                                                placeholder="Enter Other Funder"
                                                value={selectedFunder.otherFunder ?? ''}
                                                onChange={(event) => {
                                                    const value = event.target.value;

                                                    setSelectedFunder((prev) => {
                                                        return {
                                                            ...defaultFunderCsv,
                                                            ...prev,
                                                            otherFunder: value,
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>Grant ID</Form.Label>
                                            <Form.Control
                                                data-testid="grantIDInput"
                                                placeholder="Enter Grant ID"
                                                value={selectedFunder.grantID ?? ''}
                                                onChange={(event) => {
                                                    const value = event.target.value;

                                                    setSelectedFunder((prev) => {
                                                        return {
                                                            ...defaultFunderCsv,
                                                            ...prev,
                                                            grantID: value,
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>Start Date*</Form.Label>
                                            <DatePicker
                                                placeholderText="Enter Start Date"
                                                className="form-control"
                                                dateFormat="MM-dd-yyyy"
                                                selected={
                                                    selectedFunder.startDate
                                                        ? new Date(selectedFunder.startDate)
                                                        : undefined
                                                }
                                                onChange={(date: Date | null) => {
                                                    setSelectedFunder((prev) => {
                                                        return {
                                                            ...defaultFunderCsv,
                                                            ...prev,
                                                            startDate: date ? date.toISOString() : undefined,
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>End Date</Form.Label>
                                            <DatePicker
                                                placeholderText="Enter End Date"
                                                className="form-control"
                                                dateFormat="MM-dd-yyyy"
                                                selected={
                                                    selectedFunder.endDate
                                                        ? new Date(selectedFunder.endDate)
                                                        : undefined
                                                }
                                                onChange={(date: Date | null) => {
                                                    setSelectedFunder((prev) => {
                                                        return {
                                                            ...defaultFunderCsv,
                                                            ...prev,
                                                            endDate: date ? date.toISOString() : undefined,
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </Form>
                        </>
                    ) : (
                        <p>No funder records found</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default FunderEditForm;
